import React, { useCallback, useEffect, useRef, useState, memo } from "react"
import { Button, CssBaseline, TextField, Paper, makeStyles, LinearProgress, Typography } from "@material-ui/core"
import { Link, Redirect } from "react-router-dom"
import PropTypes from "prop-types"
import { ZendeskAPI } from "react-zendesk"
import { completeNewPassword } from "../../../store/actions/auth"
import SetupMFA from "../MFA/SetupMFA"
import EnterMFA from "../MFA/EnterMFA"
import logo from "../../../images/logo-dark.svg"

const useStyles = makeStyles((theme) => ({
	main: {
		width: "auto",
		display: "flex", // Fix IE 11 issue.
		flexDirection: "column",
		[theme.breakpoints.down(400 + theme.spacing(6))]: {
			width: "100%",
			boxSizing: "border-box",
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
		},
		[theme.breakpoints.up(400 + theme.spacing(6))]: {
			width: 410,
			marginLeft: "auto",
			marginRight: "auto",
		},
	},
	logo: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
		objectFit: "contain",
	},
	paper: {
		marginTop: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: theme.spacing(2, 3, 3),
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		marginTop: theme.spacing(3),
	},
	forgotPassword: {
		marginTop: theme.spacing(2),
		marginBottom: 0,
		margin: "auto",
		display: "flex",
		alignItems: "center",
	},
	trademark: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		textAlign: "center",
		fontSize: "0.6rem",
		fontWeight: 300,
		color: "darkgray",
	},
}))

const getErrorField = (errCode) => {
	switch (errCode) {
		case "InvalidPasswordException":
		case "NotAuthorizedException":
		case "PasswordResetRequiredException":
			return "errorPassword"
		case "InternalErrorException":
		case "InvalidParameterException":
		case "LimitExceededException":
		case "ResourceNotFoundException":
		case "TooManyRequestsException":
		case "UserNotConfirmedException":
		case "UserNotFoundException":
		default:
			return "errorEmail"
	}
}

const initialLoginEntries = {
	email: "",
	password: "",
}

const initialNewPasswordEntries = {
	newPassword: "",
	confirmNewPassword: "",
}

const initialAuthenticationFlow = {
	SIGN_IN: false,
	MFA_SETUP: false,
	SOFTWARE_TOKEN_MFA: false,
}

const SignIn = memo(({ location, authorized, signIn, enqueueSnackbar, closeSnackbar }) => {
	const [loginEntries, setLoginEntries] = useState({ ...initialLoginEntries })
	const [newPasswordEntries, setNewPasswordEntries] = useState({ ...initialNewPasswordEntries })
	const [errorFeedback, setErrorFeedback] = useState({})
	const [capsLockIsEnabled, setCapsLockIsEnabled] = useState(false)
	const [pendingResponse, setPendingResponse] = useState(false)
	const [newPasswordRequiredUser, setNewPasswordRequiredUser] = useState(null)
	const [authenticationFlow, setAuthenticationFlow] = useState({ ...initialAuthenticationFlow })
	const [cognitoUser, setCognitoUser] = useState(null)
	const classes = useStyles()

	const unmounted = useRef(false)

	useEffect(() => {
		return () => {
			unmounted.current = true
		}
	}, [])

	useEffect(() => {
		ZendeskAPI("webWidget", "show")
	}, [])

	const resetErrorFeedbackAndPendingResponse = useCallback(() => {
		setErrorFeedback({})
		setPendingResponse(false)
	}, [])

	// first time load, set the authentication flow to sign in
	useEffect(() => {
		setAuthenticationFlow({ ...initialAuthenticationFlow, SIGN_IN: true })
	}, [])

	// new password with confirm password validation
	useEffect(() => {
		if (newPasswordEntries.newPassword === newPasswordEntries.confirmNewPassword) {
			setErrorFeedback({})
		} else {
			setErrorFeedback({
				errorConfirmPassword: "Passwords do not match",
			})
		}
	}, [newPasswordEntries])

	const handleInputFieldChange = useCallback(
		(e) => {
			resetErrorFeedbackAndPendingResponse()
			const { id, value } = e.target
			switch (id) {
				case "email":
					setLoginEntries({ ...loginEntries, email: value })
					break
				case "password":
					setLoginEntries({ ...loginEntries, password: value })
					break
				case "newPassword":
					setNewPasswordEntries({ ...newPasswordEntries, newPassword: value })
					break
				case "confirmNewPassword":
					setNewPasswordEntries({ ...newPasswordEntries, confirmNewPassword: value })
					break
				default:
					console.error("Unknown input field name: ", name)
					break
			}
		},
		[loginEntries, newPasswordEntries, resetErrorFeedbackAndPendingResponse],
	)

	const submit = async (e) => {
		e.preventDefault()
		setErrorFeedback({})
		setPendingResponse(true)

		let timer = setTimeout(() => {
			if (unmounted.current) {
				return
			}
			setPendingResponse(false)
			setNewPasswordEntries({ ...initialNewPasswordEntries })
			setLoginEntries({ ...initialLoginEntries })
			setErrorFeedback({ errorEmail: "Request Timeout" })
		}, 20000)

		let userSession = { challengeName: "NONE" }

		try {
			if (newPasswordRequiredUser === null) {
				userSession = await signIn(loginEntries.email, loginEntries.password)
				clearTimeout(timer)
			} else {
				userSession = await completeNewPassword(newPasswordRequiredUser, newPasswordEntries.newPassword)
				enqueueSnackbar("Password successfully updated!", {
					variant: "success",
					persist: false,
					action: (key) => (
						<Button
							onClick={() => {
								closeSnackbar(key)
							}}
						>
							Dismiss
						</Button>
					),
				})
				setNewPasswordRequiredUser(null)
				clearTimeout(timer)
				if (unmounted.current) {
					return
				}
			}
		} catch (err) {
			console.error(err)
			clearTimeout(timer)
			setNewPasswordEntries({ ...initialNewPasswordEntries })
			setLoginEntries({ ...initialLoginEntries })
			setErrorFeedback({ [getErrorField(err.code)]: err.message || "An error occurred." })
			setPendingResponse(false)
		}

		switch (userSession.challengeName) {
			case "MFA_SETUP":
				setCognitoUser(userSession)
				console.log("MFA not configured, Sending to MFA Setup...")
				setAuthenticationFlow({ ...initialAuthenticationFlow, MFA_SETUP: true })
				setPendingResponse(false)
				break
			case "SMS_MFA":
				console.log("MFA SMS is currently not supported...")
				/* This is handling an extreme edge case. Where the Cognito User Pool has SMS enabled && the user has
				   a phone number in their list of attributes. If both cases are true, by default, when a user logs in,
				   the returned challengeName will be SMS_MFA or SELECT_MFA_TYPE. This is instead of an initial MFA_SETUP
				   or SOFTWARE_TOKEN_MFA. The key to avoiding this edge case is to NOT enable SMS in the Cognito User Pool until ready,
				   it is also not possible to remove SMS once enabled in AWS. It has been confirmed that removing a user's phone number
				   from their attributes will return the user to go through the Authenticator app process (Setup or Entering Code).
				 */
				enqueueSnackbar("SMS MFA is currently not supported, Please contact a GameChange Energy Administrator ", {
					variant: "error",
					persist: true,
					action: (key) => (
						<Button
							onClick={() => {
								closeSnackbar(key)
							}}
						>
							Dismiss
						</Button>
					),
				})
				setAuthenticationFlow({ ...initialAuthenticationFlow, SIGN_IN: true })
				setPendingResponse(false)
				break
			case "SOFTWARE_TOKEN_MFA":
				setCognitoUser(userSession)
				console.log("MFA configured, Sending to Software MFA Entry...")
				setAuthenticationFlow({
					...initialAuthenticationFlow,
					SOFTWARE_TOKEN_MFA: true,
				})
				setPendingResponse(false)
				break
			case "NEW_PASSWORD_REQUIRED":
				setNewPasswordRequiredUser(userSession)
				setPendingResponse(false)
				enqueueSnackbar("A new password is required to proceed.", {
					variant: "info",
					persist: false,
					action: (key) => (
						<Button
							onClick={() => {
								closeSnackbar(key)
							}}
						>
							Dismiss
						</Button>
					),
				})
				break
			case "NONE":
				setPendingResponse(false)
				console.error("No challenge name was returned. This should not happen.")
				break
			default:
				if (userSession.code) {
					setErrorFeedback({ errorEmail: userSession.message || "An unexpected error occurred" })
					setPendingResponse(false)
					return
				} else {
					const groups =
						(((userSession["signInUserSession"] || {}).accessToken || {}).payload || {})["cognito:groups"] || []
					if (groups.length === 0) {
						setLoginEntries((prevState) => ({ ...prevState, password: "" }))
						setErrorFeedback({ errorEmail: "Account has no privileges" })
						enqueueSnackbar("Account has no privileges! Contact GameChange Administrator.", {
							variant: "error",
							persist: true,
							action: (key) => (
								<Button
									onClick={() => {
										closeSnackbar(key)
									}}
								>
									Dismiss
								</Button>
							),
						})
					}
				}
				break
		}
	}

	if (authorized) {
		if (location.state && location.state.from && location.state.from.pathname !== "/") {
			return <Redirect to={location.state.from.pathname} />
		} else {
			return (
				<Redirect
					to={{
						pathname: "/layouts",
						search: "?view=list",
					}}
				/>
			)
		}
	}

	return (
		<main className={classes.main}>
			<CssBaseline />
			<img className={classes.logo} alt="GameChange Logo" src={logo} />
			{pendingResponse ? <LinearProgress /> : null}
			{authenticationFlow.SIGN_IN && (
				<Paper className={classes.paper}>
					<div className={classes.trademark}>
						Welcome to the GENIUS TRACKER&reg; monitoring and control system with GENIUSVISION&trade;,
						WEATHERSMART&trade;, POWERSMART&trade;, SMARTSTOW&trade;, THUNDERSMART&trade;, and SNOWSHIELD&trade; solar
						tracker control technology!
					</div>
					<Typography component="h1" variant="h5">
						Sign in
					</Typography>
					<form className={classes.form} onSubmit={submit}>
						{newPasswordRequiredUser !== null ? (
							<>
								<TextField
									key="newPassword"
									id="newPassword"
									label="New Password"
									type="password"
									margin="normal"
									variant="outlined"
									value={newPasswordEntries.newPassword}
									onKeyDown={(e) => setCapsLockIsEnabled(e.getModifierState("CapsLock"))}
									onChange={handleInputFieldChange}
									autoComplete="new-password"
									error={errorFeedback["errorPassword"] !== undefined}
									helperText={errorFeedback["errorPassword"] || (capsLockIsEnabled && "CapsLock Is Enabled")}
									autoFocus
									required
									fullWidth
								/>
								<TextField
									key="confirmNewPassword"
									id="confirmNewPassword"
									label="Confirm New Password"
									type="password"
									margin="normal"
									variant="outlined"
									value={newPasswordEntries.confirmNewPassword}
									onKeyDown={(e) => setCapsLockIsEnabled(e.getModifierState("CapsLock"))}
									onChange={handleInputFieldChange}
									autoComplete="confirm-new-password"
									error={errorFeedback.errorConfirmPassword !== undefined}
									helperText={errorFeedback.errorConfirmPassword || (capsLockIsEnabled && "CapsLock Is Enabled")}
									required
									fullWidth
								/>
							</>
						) : (
							<>
								<TextField
									key="email"
									id="email"
									label="Email Address"
									type="email"
									margin="normal"
									variant="outlined"
									value={loginEntries.email}
									onChange={handleInputFieldChange}
									autoComplete="email"
									error={errorFeedback.errorEmail !== undefined}
									helperText={errorFeedback.errorEmail}
									autoFocus
									required
									fullWidth
								/>
								<TextField
									key="password"
									id="password"
									label="Password"
									type="password"
									margin="normal"
									variant="outlined"
									value={loginEntries.password}
									onKeyDown={(e) => setCapsLockIsEnabled(e.getModifierState("CapsLock"))}
									onChange={handleInputFieldChange}
									autoComplete="current-password"
									error={errorFeedback["errorPassword"] !== undefined}
									helperText={errorFeedback["errorPassword"] || (capsLockIsEnabled && "CapsLock Is Enabled")}
									required
									fullWidth
								/>
							</>
						)}
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							disabled={pendingResponse}
						>
							{newPasswordRequiredUser !== null ? "Submit" : "Sign in"}
						</Button>
						{newPasswordRequiredUser === null && (
							<Button color="primary" className={classes.forgotPassword} component={Link} to="/reset-password">
								Forgot Password
							</Button>
						)}
					</form>
				</Paper>
			)}
			{authenticationFlow.MFA_SETUP && <SetupMFA cognitoUser={cognitoUser} loginEntries={loginEntries} />}
			{authenticationFlow.SOFTWARE_TOKEN_MFA && (
				<EnterMFA cognitoUser={cognitoUser} loginEntries={loginEntries} authenticationFlowStep={"SOFTWARE_TOKEN_MFA"} />
			)}
		</main>
	)
})

SignIn.propTypes = {
	location: PropTypes.any,
	authorized: PropTypes.any,
	signIn: PropTypes.func,
	enqueueSnackbar: PropTypes.func,
	closeSnackbar: PropTypes.any,
}

SignIn.displayName = "SignIn"

export default SignIn
